<template>
  <FormularioCadastro
    module-name="Regras de Acesso"
    :is-loading="isLoading"
    :is-editing="isEditing"
    :entity-info="entityInfo"
    @submit="submitFormComExportacao"
    @submit-continuar="submitFormContinuar"
    @cancelar="handleCancel"
    @show-notification="(payload) => emit('show-notification', payload)"
  >
    <!-- Frame 1: Dados Básicos -->
    <div class="card mb-4">
      <div class="card-header">
        <h5 class="mb-0"><i class="bi bi-tags me-2 text-primary"></i> Dados Básicos</h5>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-4">
            <CampoInput
              id="modulo"
              label="Módulo"
              v-model="item.modulo"
              required
              placeholder="Ex: Usuarios, Produtos, Sistema"
            />
          </div>

          <div class="col-md-4">
            <CampoInput
              id="chave"
              label="Chave"
              v-model="item.chave"
              required
              placeholder="Ex: EMBARQUES"
            />
          </div>

          <div class="col-md-4">
            <CampoInput
              id="titulo"
              label="Título (Menu)"
              v-model="item.titulo"
              placeholder="Ex: Embarques de Importação"
            />
          </div>

          <div class="col-md-4">
            <CampoInput
              id="descricao"
              label="Descrição"
              v-model="item.descricao"
              required
              placeholder="Ex: Gestão de embarques"
            />
          </div>

          <div class="col-md-4">
            <CampoInput
              id="icone"
              label="Ícone"
              v-model="item.icone"
              placeholder="Ex: bi-truck"
              descricao-informativa="Classe do Bootstrap Icons (ex: bi-truck)."
            />
          </div>

          <div class="col-md-4">
            <CampoInput
              id="ordem"
              label="Ordem"
              type="decimal"
              :casas-decimais="0"
              v-model="item.ordem"
              placeholder="Ex: 1"
            />
          </div>

          <div class="col-md-6">
            <CampoBusca
              id="idRegraAcessoPai"
              label="Regra Pai (Grupo)"
              v-model="item.idRegraAcessoPai"
              :configuracao-busca="configuracaoBuscaRegraPai"
              placeholder="Buscar regra pai..."
              descricao-informativa="Ao informar, esta regra vira filha do grupo selecionado."
            />
          </div>

          <div class="col-md-2">
            <CampoSelect
              id="isGrupo"
              label="É Grupo?"
              v-model="item.isGrupo"
              :options="simNaoOptions"
              option-value="id"
              option-label="label"
              dica="Gera um nó de grupo no menu (ex: Importação)."
            />
          </div>

          <div class="col-md-2">
            <CampoSelect
              id="isHeader"
              label="É Header?"
              v-model="item.isHeader"
              :options="simNaoOptions"
              option-value="id"
              option-label="label"
              dica="Cria um separador visual (subseção) dentro de um grupo."
            />
          </div>

          <div class="col-md-2">
            <CampoSelect
              id="isRegraParaUsuario"
              label="Liberado p/ Cliente?"
              v-model="item.isRegraParaUsuario"
              :options="simNaoOptions"
              option-value="id"
              option-label="label"
              dica="true = AdminCliente acessa; false = apenas AdminInterno."
            />
          </div>

          <div class="col-md-2">
            <CampoSelect
              id="ativo"
              label="Ativo?"
              v-model="item.ativo"
              :options="simNaoOptions"
              option-value="id"
              option-label="label"
            />
          </div>

          <!-- Tela de Abertura (IdDocumentacaoTelaDefault) — visível apenas para nível 3 -->
          <div v-if="isNivelTres" class="col-md-6">
            <CampoBusca
              id="idDocumentacaoTelaDefault"
              label="Tela de Abertura"
              v-model="item.idDocumentacaoTelaDefault"
              :configuracao-busca="configuracaoBuscaDocumentacaoTela"
              placeholder="Buscar tela de abertura..."
              descricao-informativa="Tela que o menu abre por padrão. Obrigatório para menus de acesso a tela (nível 3)."
            />
          </div>

          <!-- ExtraPropsJson — append-only, readonly após salvar -->
          <div v-if="isNivelTres" class="col-md-6">
            <CampoInput
              id="extraPropsJson"
              label="Props Extras (JSON)"
              v-model="item.extraPropsJson"
              :readonly="isEditing"
              placeholder='Ex: {"tipoOperacao":"COMEX_TP_OPER1"}'
              descricao-informativa="JSON com propriedades extras passadas ao componente. Append-only: não editável após salvar."
            />
          </div>
        </div>
      </div>
    </div> <!-- Fim Frame 1 -->

    <!-- Frame 2: Permissões Granulares -->
    <div class="card mb-4">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0"><i class="bi bi-shield-check me-2 text-primary"></i> Permissões</h5>
        <div class="d-flex align-items-center gap-2">
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            @click="toggleExpandTodasPermissoes"
            :disabled="isFrameBlocked || item.permissoes.length === 0"
            :title="todasPermissoesExpandidas ? 'Recolher todas as permissões' : 'Expandir todas as permissões'"
          >
            <i :class="['bi', todasPermissoesExpandidas ? 'bi-chevron-contract' : 'bi-chevron-expand']"></i>
          </button>
          <button
            type="button"
            class="btn btn-sm btn-primary"
            @click="abrirModalPermissao()"
            :disabled="isFrameBlocked"
          >
            <i class="bi bi-plus-lg me-1"></i> Adicionar Permissão
          </button>
        </div>
      </div>
      <div class="card-body">
        <div v-if="isFrameBlocked" class="alert alert-warning border-0 shadow-sm mb-3">
          <i class="bi bi-info-circle me-2"></i>
          Salve a Regra de Acesso primeiro para poder gerenciar as permissões.
        </div>

        <div v-if="loadingPermissoes" class="text-center text-muted small py-4">
          <span class="spinner-border spinner-border-sm me-2"></span> A carregar permissões...
        </div>

        <div v-else class="table-responsive">
          <table class="table table-sm table-hover align-middle">
            <thead class="table-light">
              <tr>
                <th style="width: 70px;">ID</th>
                <th style="width: 30px;"></th>
                <th>Chave</th>
                <th>Descrição</th>
                <th class="text-end" style="width: 100px;">Ações</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="permissao in item.permissoes" :key="permissao.idRegraAcessoPermissao">
                <tr>
                  <td class="text-muted small">
                    {{ permissao.idRegraAcessoPermissao }}
                  </td>
                  <td class="text-center">
                    <button
                      v-if="permissao.telasVinculadas && permissao.telasVinculadas.length > 0"
                      type="button"
                      class="btn btn-sm p-0 border-0 bg-transparent"
                      @click="toggleExpandPermissao(permissao.idRegraAcessoPermissao)"
                    >
                      <i :class="['bi', expandedPermissaoRows[permissao.idRegraAcessoPermissao] ? 'bi-chevron-down' : 'bi-chevron-right', 'text-secondary']"></i>
                    </button>
                  </td>
                  <td>
                    <span class="fw-medium text-body-emphasis">{{ permissao.chave }}</span>
                  </td>
                  <td>
                    <span>{{ permissao.descricao || '-' }}</span>
                  </td>
                  <td class="text-end">
                    <div class="d-flex justify-content-end gap-1 acoes-wrapper">
                      <button type="button" class="btn-icon" @click="abrirModalPermissao(permissao)" title="Editar">
                        <i class="bi bi-pencil-fill text-primary"></i>
                      </button>
                      <button type="button" class="btn-icon" @click="solicitarExclusaoPermissao(permissao)" title="Excluir">
                        <i class="bi bi-trash-fill text-danger"></i>
                      </button>
                    </div>
                  </td>
                </tr>
                <!-- Sub-linha expansível com as telas vinculadas -->
                <tr v-if="expandedPermissaoRows[permissao.idRegraAcessoPermissao] && permissao.telasVinculadas && permissao.telasVinculadas.length > 0">
                  <td colspan="5" class="p-0 bg-body-tertiary">
                    <div class="px-4 py-2 border-start border-2 border-primary ms-3 my-1">
                      <div v-for="tela in permissao.telasVinculadas" :key="tela.idDocumentacaoTela" class="d-flex align-items-center gap-2 mb-1">
                        <span class="badge badge-ghost-info">{{ tela.idDocumentacao || '-' }}</span>
                        <span class="fw-medium small">{{ tela.tela || '-' }}</span>
                        <span class="text-muted small" v-if="tela.modulo">
                          <i class="bi bi-grid me-1"></i>{{ tela.modulo }}
                        </span>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
              <tr v-if="item.permissoes.length === 0">
                <td colspan="5" class="text-center text-muted py-3">
                  Nenhuma permissão cadastrada.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div> <!-- Fim Frame 2 -->

    <!-- Modal: Criar/Editar Permissão -->
    <ModalFormularioV2
      v-model="showModalPermissao"
      :titulo="permissaoEmEdicao?.idRegraAcessoPermissao ? 'Editar Permissão' : 'Nova Permissão'"
      tamanho="lg"
      cor-header="var(--bs-primary)"
      :loading="savingPermissao"
      @confirmar="salvarPermissao"
    >
      <div class="row g-3">
        <div class="col-md-6">
          <CampoInput
            id="permissaoChave"
            label="Chave"
            v-model="permissaoEmEdicao.chave"
            required
            placeholder="Ex: EMBARQUES_COMPLETO"
          />
        </div>

        <div class="col-md-6">
          <CampoInput
            id="permissaoDescricao"
            label="Descrição"
            v-model="permissaoEmEdicao.descricao"
            placeholder="Ex: Permite CRUD completo"
          />
        </div>
      </div>

      <hr class="my-3" />

      <!-- Telas vinculadas a esta permissão -->
      <h6 class="mb-2"><i class="bi bi-journal-text me-1 text-warning"></i> Telas Liberadas por esta Permissão</h6>
      <p class="text-muted small mb-2">
        Selecione as telas documentadas que esta permissão libera.
      </p>

      <CampoBusca
        id="buscaDocumentacaoTela"
        label="Buscar Tela Documentada"
        v-model="telaSelecionadaId"
        :configuracao-busca="configuracaoBuscaDocumentacaoTela"
        @selecionado="adicionarTelaVinculada"
        hide-label
      />

      <div class="table-responsive mt-2">
        <table class="table table-sm align-middle">
          <thead>
            <tr>
              <th class="text-muted small">ID Documentação</th>
              <th class="text-muted small">Tela</th>
              <th class="text-muted small">Módulo</th>
              <th class="text-end text-muted small">Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="permissaoEmEdicao.telasVinculadas.length === 0">
              <td colspan="4" class="text-center text-muted py-3">
                Nenhuma tela vinculada. Use a busca acima para adicionar.
              </td>
            </tr>
            <tr v-for="tela in permissaoEmEdicao.telasVinculadas" :key="tela.idDocumentacaoTela">
              <td class="fw-medium">{{ tela.idDocumentacao }}</td>
              <td>{{ tela.tela }}</td>
              <td>{{ tela.modulo }}</td>
              <td class="text-end">
                <button type="button" class="btn-icon" @click="removerTelaVinculada(tela.idDocumentacaoTela)" title="Remover">
                  <i class="bi bi-x-lg text-danger"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </ModalFormularioV2>

    <!-- Modal de Confirmação de Exclusão de Permissão -->
    <ConfirmationModal
      ref="confirmacaoExclusaoRef"
      title="Excluir Permissão"
      :message="`Deseja realmente excluir a permissão <strong>${permissaoParaExcluir?.chave || ''}</strong>?`"
      confirm-button-text="Excluir"
      cancel-button-text="Cancelar"
      @confirmed="confirmarExclusaoPermissao"
    />

  </FormularioCadastro>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue'
import { useFormulario } from '@/composables/useFormulario.js'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import ModalFormularioV2 from '@/components/base/modal/ModalFormularioV2.vue'
import ConfirmationModal from '@/components/base/modal/ConfirmationModal.vue'
import { criarConfigBuscaRegraAcesso } from '@/search-definitions/regraAcessoSearch'
import { criarConfigBuscaDocumentacaoTela } from '@/search-definitions/documentacaoTelaSearch'
import api from '@/services/api'

const props = defineProps({
  itemParaEditar: { type: Object, default: null },
  modeloVazio: {
    type: Object,
    default: () => ({
      idRegraAcesso: null,
      modulo: '',
      chave: '',
      descricao: '',
      titulo: '',
      icone: '',
      ordem: null,
      idRegraAcessoPai: null,
      isGrupo: false,
      isHeader: false,
      isRegraParaUsuario: false,
      ativo: true,
      idDocumentacaoTelaDefault: null,
      extraPropsJson: '',
      permissoes: []
    })
  }
})

const emit = defineEmits(['itemAtualizado', 'cancelar', 'changesDetected', 'show-notification'])

const {
  item,
  isLoading,
  isEditing,
  entityInfo,
  errors,
  submitForm,
  handleCancel,
  focarPrimeiroErro,
  initializeForm,
  validarCamposObrigatoriosFrontEnd
} = useFormulario(props, emit, { idKey: 'idRegraAcesso' })

// --- Opções Sim/Não (regra: booleanos usam CampoSelect, nunca checkbox) ---
const simNaoOptions = [
  { id: true, label: 'Sim' },
  { id: false, label: 'Não' }
]

// --- Search Definitions ---
const configuracaoBuscaRegraPai = criarConfigBuscaRegraAcesso()
const configuracaoBuscaDocumentacaoTela = criarConfigBuscaDocumentacaoTela()

// --- Expansão de linha (telas vinculadas às permissões) ---
const expandedPermissaoRows = reactive({})

const toggleExpandPermissao = (id) => {
  expandedPermissaoRows[id] = !expandedPermissaoRows[id]
}

const todasPermissoesExpandidas = computed(() => {
  const comTelas = item.value.permissoes.filter(
    p => p.telasVinculadas && p.telasVinculadas.length > 0
  )
  return comTelas.length > 0 && comTelas.every(p => expandedPermissaoRows[p.idRegraAcessoPermissao])
})

// --- Nível 3: !isGrupo && !isHeader → exibe campos de Tela de Abertura e Props Extras ---
const isNivelTres = computed(() => !item.value.isGrupo && !item.value.isHeader)

const toggleExpandTodasPermissoes = () => {
  const alvo = !todasPermissoesExpandidas.value
  for (const permissao of item.value.permissoes) {
    if (permissao.telasVinculadas && permissao.telasVinculadas.length > 0) {
      expandedPermissaoRows[permissao.idRegraAcessoPermissao] = alvo
    }
  }
}

// --- Padrão "Salvar e Continuar": Frame 2 bloqueado até o pai ser salvo ---
const isHeaderSaved = ref(isEditing.value)
const isFrameBlocked = computed(() => !isHeaderSaved.value)

// --- Estado do Modal de Permissão ---
const showModalPermissao = ref(false)
const savingPermissao = ref(false)
const loadingPermissoes = ref(false)
const telaSelecionadaId = ref(null)

const permissaoEmEdicao = ref({
  idRegraAcessoPermissao: null,
  chave: '',
  descricao: '',
  telasVinculadas: []
})

// --- Estado da exclusão com ConfirmationModal ---
const confirmacaoExclusaoRef = ref(null)
const permissaoParaExcluir = ref(null)

// Quando o item é carregado (edição), desbloqueia o frame
watch(() => item.value.idRegraAcesso, (novoId) => {
  if (novoId) {
    isHeaderSaved.value = true
  }
})

// GET dedicado ao editar: busca o grafo completo da regra de acesso
// (RegraAcesso → RegraAcessoPermissao → DocumentacaoTelaPermissao → DocumentacaoTela)
// para alimentar corretamente o grid de permissões com as telas vinculadas.
watch(() => props.itemParaEditar?.idRegraAcesso, async (novoId) => {
  if (!novoId) return
  try {
    isLoading.value = true
    const response = await api.get(`/RegrasAcesso/${novoId}`)
    if (response?.data) {
      initializeForm(response.data)
      isHeaderSaved.value = true
    }
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao carregar os dados da regra de acesso.'
    if (typeof data === 'string') {
      msg = data
    } else if (data?.message) {
      msg = data.message
    } else if (data?.title) {
      msg = data.title
    } else if (data?.errors) {
      msg = Object.values(data.errors).flat().join('; ')
    }
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoading.value = false
  }
}, { immediate: true })

const abrirModalPermissao = (permissao = null) => {
  if (permissao) {
    permissaoEmEdicao.value = {
      idRegraAcessoPermissao: permissao.idRegraAcessoPermissao,
      chave: permissao.chave || '',
      descricao: permissao.descricao || '',
      telasVinculadas: permissao.telasVinculadas ? [...permissao.telasVinculadas] : []
    }
  } else {
    permissaoEmEdicao.value = {
      idRegraAcessoPermissao: null,
      chave: '',
      descricao: '',
      telasVinculadas: []
    }
  }
  telaSelecionadaId.value = null
  showModalPermissao.value = true
}

const adicionarTelaVinculada = (tela) => {
  if (!tela || !tela.idDocumentacaoTela) return
  const jaExiste = permissaoEmEdicao.value.telasVinculadas.some(
    t => String(t.idDocumentacaoTela) === String(tela.idDocumentacaoTela)
  )
  if (jaExiste) {
    emit('show-notification', { message: 'Esta tela já está vinculada à permissão.', type: 'warning' })
    return
  }
  permissaoEmEdicao.value.telasVinculadas.push({
    idDocumentacaoTela: tela.idDocumentacaoTela,
    idDocumentacao: tela.idDocumentacao,
    tela: tela.tela,
    modulo: tela.modulo
  })
  telaSelecionadaId.value = null
}

const removerTelaVinculada = (idDocumentacaoTela) => {
  const idx = permissaoEmEdicao.value.telasVinculadas.findIndex(
    t => String(t.idDocumentacaoTela) === String(idDocumentacaoTela)
  )
  if (idx >= 0) {
    permissaoEmEdicao.value.telasVinculadas.splice(idx, 1)
  }
}

const salvarPermissao = async () => {
  if (!permissaoEmEdicao.value.chave.trim()) {
    emit('show-notification', { message: 'A Chave da permissão é obrigatória.', type: 'warning' })
    return
  }

  savingPermissao.value = true
  try {
    const idRegraAcesso = item.value.idRegraAcesso
    const payload = {
      chave: permissaoEmEdicao.value.chave.trim(),
      descricao: permissaoEmEdicao.value.descricao || null
    }

    let permissaoSalva
    if (permissaoEmEdicao.value.idRegraAcessoPermissao) {
      // Edição: usa PUT direto na permissão (sem DELETE+POST)
      const response = await api.put(`/RegraAcessoPermissoes/${permissaoEmEdicao.value.idRegraAcessoPermissao}`, payload)
      permissaoSalva = response.data
    } else {
      // Criação
      const response = await api.post(`/RegrasAcesso/${idRegraAcesso}/permissoes`, payload)
      permissaoSalva = response.data
    }

    // Salva telas vinculadas (Problema 4)
    const idsTelas = permissaoEmEdicao.value.telasVinculadas.map(t => t.idDocumentacaoTela)
    if (idsTelas.length > 0) {
      await api.put(`/RegraAcessoPermissoes/${permissaoSalva.idRegraAcessoPermissao}/telas`, idsTelas)
    } else if (permissaoEmEdicao.value.idRegraAcessoPermissao) {
      // Se removeu todas as telas, envia array vazio para limpar
      await api.put(`/RegraAcessoPermissoes/${permissaoSalva.idRegraAcessoPermissao}/telas`, [])
    }

    // Atualiza a lista local
    if (permissaoEmEdicao.value.idRegraAcessoPermissao) {
      const idx = item.value.permissoes.findIndex(
        p => String(p.idRegraAcessoPermissao) === String(permissaoEmEdicao.value.idRegraAcessoPermissao)
      )
      if (idx >= 0) {
        item.value.permissoes.splice(idx, 1, { ...permissaoSalva, telasVinculadas: permissaoEmEdicao.value.telasVinculadas })
      }
    } else {
      item.value.permissoes.push({ ...permissaoSalva, telasVinculadas: permissaoEmEdicao.value.telasVinculadas })
    }

    showModalPermissao.value = false
    emit('show-notification', { message: 'Permissão salva com sucesso!', type: 'success' })
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao salvar permissão.'
    if (typeof data === 'string') {
      msg = data
    } else if (data?.message) {
      msg = data.message
    } else if (data?.title) {
      msg = data.title
    } else if (data?.errors) {
      msg = Object.values(data.errors).flat().join('; ')
    }
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    savingPermissao.value = false
  }
}

const solicitarExclusaoPermissao = (permissao) => {
  permissaoParaExcluir.value = permissao
  confirmacaoExclusaoRef.value?.show()
}

const confirmarExclusaoPermissao = async (confirmado) => {
  if (!confirmado || !permissaoParaExcluir.value) {
    permissaoParaExcluir.value = null
    return
  }

  try {
    const permissao = permissaoParaExcluir.value
    await api.delete(`/RegrasAcessoPermissoes/${permissao.idRegraAcessoPermissao}`)
    const idx = item.value.permissoes.findIndex(
      p => String(p.idRegraAcessoPermissao) === String(permissao.idRegraAcessoPermissao)
    )
    if (idx >= 0) {
      item.value.permissoes.splice(idx, 1)
    }
    permissaoParaExcluir.value = null
    emit('show-notification', { message: 'Permissão excluída com sucesso!', type: 'success' })
  } catch (error) {
    permissaoParaExcluir.value = null
    const data = error.response?.data
    let msg = 'Erro ao excluir permissão.'
    if (typeof data === 'string') {
      msg = data
    } else if (data?.message) {
      msg = data.message
    } else if (data?.title) {
      msg = data.title
    } else if (data?.errors) {
      msg = Object.values(data.errors).flat().join('; ')
    }
    emit('show-notification', { message: msg, type: 'error' })
  }
}

async function submitFormComExportacao () {
  const sucesso = await salvarRegraEExportarSeed()
  if (sucesso) {
    emit('itemAtualizado', item.value)
  }
}

async function submitFormContinuar () {
  // Validação de campos obrigatórios (frontend)
  errors.value = {}
  if (!validarCamposObrigatoriosFrontEnd()) {
    const primeiraMensagemErro = Object.values(errors.value)[0] || 'Por favor, preencha todos os campos obrigatórios destacados em vermelho.'
    emit('show-notification', { message: primeiraMensagemErro, type: 'warning' })
    await focarPrimeiroErro()
    return
  }

  isLoading.value = true
  try {
    let response
    if (isEditing.value) {
      response = await api.put(`/RegrasAcesso/${item.value.idRegraAcesso}`, item.value)
    } else {
      response = await api.post('/RegrasAcesso', item.value)
    }

    if (response?.data) {
      item.value = response.data
      isHeaderSaved.value = true
    }

    emit('show-notification', { message: 'Regra de acesso salva com sucesso!', type: 'success' })

    // Exporta seed (não crítico)
    try {
      await api.post('/Setup/exportar-seed-regras-acesso')
      console.log('[RegrasAcesso] Seed exportado com sucesso após salvar regra.')
    } catch (error) {
      console.warn('[RegrasAcesso] Erro ao exportar seed (não crítico):', error)
    }
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao salvar regra de acesso.'
    if (typeof data === 'string') {
      msg = data
    } else if (data?.message) {
      msg = data.message
    } else if (data?.title) {
      msg = data.title
    } else if (data?.errors) {
      msg = Object.values(data.errors).flat().join('; ')
    }
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoading.value = false
  }
}

async function salvarRegraEExportarSeed () {
  const sucesso = await submitForm('RegrasAcesso', 'idRegraAcesso')
  if (sucesso) {
    isHeaderSaved.value = true
    emit('show-notification', { message: 'Regra de acesso salva com sucesso!', type: 'success' })
    try {
      await api.post('/Setup/exportar-seed-regras-acesso')
      console.log('[RegrasAcesso] Seed exportado com sucesso após salvar regra.')
    } catch (error) {
      console.warn('[RegrasAcesso] Erro ao exportar seed (não crítico):', error)
    }
  }
  return sucesso
}
</script>

<!-- GUARDIÃO: O bloco <style scoped> inteiro foi removido!
     A estilização dos frames (.card, .card-header, .card-body)
     agora é herdada globalmente e de forma inteligente do FormularioCadastro.vue -->
